import { lazy, Suspense } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import ProtectedRoute from 'components/Auth/ProtectedRoute'
import useInstitution from 'hooks/useInstitution'
import { getInstitutionLogo } from 'utils/institutionBranding'
import { isPublicCertificatePath } from 'utils/publicCertificatePath'

const RegisterForm = lazy(() => import('components/Forms/RegisterForm/RegisterForm'))
const Login = lazy(() => import('pages/Login/Login'))
const MainPage = lazy(() => import('pages/MainPage/MainPage'))
const StatementPage = lazy(() => import('pages/StatementPage/StatementPage'))
const TasksPage = lazy(() => import('pages/TasksPage/TasksPage'))
const ChatPageContainer = lazy(() => import('pages/ChatPage/ChatPageContainer'))
const StatementInfo = lazy(() => import('pages/StatementPage/components/StatementTable/StatementInfo/StatementInfo'))
const TaskItemInfo = lazy(() => import('pages/TasksPage/components/TaskItem/TaskItemInfo/TaskItemInfo'))
const TaskTree = lazy(() => import('hooks/TaskTree/TaskTree'))
const QuizForm = lazy(() => import('components/Forms/QuizForm/QuizForm'))
const StructureForm = lazy(() => import('components/Forms/StructureForm/StructureForm'))
const CalendarPage = lazy(() => import('pages/CalendarPage/CalendarPage'))
const AllStatisticPage = lazy(() => import('pages/StatisticPage/AllStatisticPage'))
const StatisticPage = lazy(() => import('pages/StatisticPage/StatisticPage'))
const StructureInfo = lazy(() => import('pages/StructurePage/components/StructureInfo'))
const StructurePage = lazy(() => import('pages/StructurePage/StructurePage'))
const ArchivePage = lazy(() => import('pages/ArchivePage/ArchivePage'))
const ArchiveDetail = lazy(() => import('pages/ArchivePage/ArchiveDetail'))
const Europass = lazy(() => import('pages/ProfilePage/Europass/Europass'))
const FinalEuropass = lazy(() => import('pages/ProfilePage/Europass/FinalEuropass'))
const PersonalCard = lazy(() => import('pages/ProfilePage/PersonalCard/PersonalCard'))
const EuropassEn = lazy(() => import('pages/ProfilePage/Europass/EuropassEn/EuropassEn'))
const FinalEuropassEn = lazy(() => import('pages/ProfilePage/Europass/EuropassEn/FinalEuropassEn'))
const EuropassKy = lazy(() => import('pages/ProfilePage/Europass/EuropassKy/EuropassKy'))
const FinalEuropassKy = lazy(() => import('pages/ProfilePage/Europass/EuropassKy/FinalEuropassKy'))
const AdminPage = lazy(() => import('pages/AdminPage/AdminPage'))
const ReportCard = lazy(() => import('pages/ReportCard/ReportCard'))
const NotesPage = lazy(() => import('pages/NotesPage/NotesPage'))
const TestPage = lazy(() => import('pages/TestPage/TestPage'))
const TestContent = lazy(() => import('pages/TestPage/components/components/TestContent'))
const PartWorkUsed = lazy(() => import('pages/TestPage/components/usedcomponents/PartWorkUsed'))
const PartWorkUsedAllUser = lazy(() => import('pages/TestPage/components/usedcomponents/PartWorkUsedAllUser'))
const SurveyPage = lazy(() => import('pages/SurveyPage/SurveyPage'))
const SurveyContent = lazy(() => import('pages/SurveyPage/components/components/SurveyContent'))
const PartSurveyUsed = lazy(() => import('pages/SurveyPage/components/usedcomponents/PartSurveyUsed'))
const PartSurveyUsedAllUser = lazy(() => import('pages/SurveyPage/components/usedcomponents/PartSurveyUsedAllUser'))
const AccountingPage = lazy(() => import('pages/AccountingPage/AccountingPage'))
const AccountingDataPage = lazy(() => import('pages/AccountingPage/AccountingDataPage'))
const StudentDashboard = lazy(() => import('pages/StudentDashboard/StudentDashboard'))
const PublicCertificatePage = lazy(() => import('pages/PublicCertificate/PublicCertificatePage'))
const ApplicantHome = lazy(() => import('pages/ApplicantHome/ApplicantHome'))
const StudentFinancePage = lazy(() => import('pages/StudentFinance/StudentFinancePage'))
const TeacherDashboard = lazy(() => import('pages/TeacherDashboard/TeacherDashboard'))
const SchedulePage = lazy(() => import('pages/SchedulePage/SchedulePage'))
const ScheduleBuilderPage = lazy(() => import('pages/SchedulePage/ScheduleBuilderPage'))
const SyllabusViewPage = lazy(() => import('pages/SyllabusViewPage/SyllabusViewPage'))
const JournalPage = lazy(() => import('pages/JournalPage/JournalPage'))
const GradebookPage = lazy(() => import('pages/GradebookPage/GradebookPage'))
const AssignmentsPage = lazy(() => import('pages/AssignmentsPage/AssignmentsPage'))
const StatementsPage = lazy(() => import('pages/StatementsPage/StatementsPage'))
const AdminFullPage = lazy(() => import('pages/AdminFullPage/AdminFullPage'))
const AccountLockedPage = lazy(() => import('pages/Auth/AccountLockedPage'))
const RoleHomeRedirect = lazy(() => import('pages/Auth/RoleHomeRedirect'))
const OrdersPage = lazy(() => import('pages/OrdersPage/OrdersPage'))
const GroupsPage = lazy(() => import('pages/GroupsPage/GroupsPage'))
const InstitutionStructurePage = lazy(() => import('pages/InstitutionStructure/InstitutionStructurePage'))
const WorkRequestsPage = lazy(() => import('pages/WorkRequestsPage/WorkRequestsPage'))
const LibraryPage = lazy(() => import('pages/LibraryPage/LibraryPage'))
const DormPage = lazy(() => import('pages/DormPage/DormPage'))
const PracticePage = lazy(() => import('pages/PracticePage/PracticePage'))
const DiplomaPage = lazy(() => import('pages/DiplomaPage/DiplomaPage'))
const TestTakingPage = lazy(() => import('pages/TestPage/TestTakingPage'))
const TestBuilderPage = lazy(() => import('pages/TestPage/TestBuilderPage'))
const QuestionBankPage = lazy(() => import('pages/TestPage/QuestionBankPage'))
const DeanDashboard = lazy(() => import('pages/DeanDashboard/DeanDashboard'))
const SubjectPage = lazy(() => import('pages/SubjectPage/SubjectPage'))
const EmployeeWizardPage = lazy(() => import('pages/HR/EmployeeWizardPage'))
const EmployeeCardPage = lazy(() => import('pages/HR/EmployeeCardPage'))
const StudentLifecyclePage = lazy(() => import('pages/HR/StudentLifecyclePage'))
const SyllabusBuilderPage = lazy(() => import('pages/SyllabusBuilder/SyllabusBuilderPage'))
const SyllabusDiffPage = lazy(() => import('pages/SyllabusBuilder/SyllabusDiffPage'))
const SyllabusApprovalPage = lazy(() => import('pages/SyllabusBuilder/SyllabusApprovalPage'))
const ExamSessionsPage = lazy(() => import('pages/Sessions/ExamSessionsPage'))
const LessonRescheduleModal = lazy(() => import('pages/SchedulePage/LessonRescheduleModal'))
const ScheduleImportPage = lazy(() => import('pages/SchedulePage/ScheduleImportPage'))
const AbsenceJustificationPage = lazy(() => import('pages/Journal/AbsenceJustificationPage'))
const CuratorDrilldownPage = lazy(() => import('pages/DeanDashboard/CuratorDrilldownPage'))
const ReportsPage = lazy(() => import('pages/Reports/ReportsPage'))
const Form1NKPage = lazy(() => import('pages/Reports/Form1NKPage'))
const ContingentReportPage = lazy(() => import('pages/Reports/ContingentReportPage'))
const AccreditationReportPage = lazy(() => import('pages/Reports/AccreditationReportPage'))
const DistanceLearningPage = lazy(() => import('pages/DistanceLearning/DistanceLearningPage'))
const VideoLecturesPage = lazy(() => import('pages/DistanceLearning/VideoLecturesPage'))
const VirtualClassroomPage = lazy(() => import('pages/DistanceLearning/VirtualClassroomPage'))
const PostgradPage = lazy(() => import('pages/Postgrad/PostgradPage'))
const ForeignStudentsPage = lazy(() => import('pages/ForeignStudents/ForeignStudentsPage'))
const MilitaryStudentPage = lazy(() => import('pages/Military/MilitaryStudentPage'))
const DpoPage = lazy(() => import('pages/Dpo/DpoPage'))
const StudentLifePage = lazy(() => import('pages/StudentLife/StudentLifePage'))
const SciencePage = lazy(() => import('pages/Science/SciencePage'))
const CareerPage = lazy(() => import('pages/Career/CareerPage'))
const PlagiarismPage = lazy(() => import('pages/Plagiarism/PlagiarismPage'))
const OcrSearchPage = lazy(() => import('pages/Archive/OcrSearchPage'))
const OrderArchiveLinkPage = lazy(() => import('pages/Archive/OrderArchiveLinkPage'))
const HelpPage = lazy(() => import('pages/Help/HelpPage'))
const ApiDocsPage = lazy(() => import('pages/Help/ApiDocsPage'))
const IntegrationsPage = lazy(() => import('pages/Integrations/IntegrationsPage'))
const ServicesPage = lazy(() => import('pages/Services/ServicesPage'))

const ADMIN_ROLES = ['rectorate', 'sysadmin', 'methodist', 'dean', 'head_of_department', 'hr_students', 'hr_employees', 'accountant', 'archivist']
const HR_STUDENTS = ['hr_students', 'methodist', 'dean', 'rectorate', 'sysadmin']
const HR_EMPLOYEES = ['hr_employees', 'rectorate', 'sysadmin']
const METHODIST = ['methodist', 'dean', 'head_of_department', 'rectorate', 'sysadmin']
const DEAN = ['dean', 'rectorate', 'sysadmin']
// Wave 4: ролевые группы для приватных страниц.
const TEACHER_HOME_ROLES = ['teacher', 'dean', 'methodist', 'curator', 'head_of_department']
const JOURNAL_ROLES = ['teacher', 'dean', 'curator', 'methodist', 'head_of_department', 'student']
const GRADEBOOK_ROLES = ['teacher', 'dean', 'methodist', 'student', 'curator', 'parent', 'head_of_department']
const DORM_ROLES = ['dorm_staff', 'hr_students', 'hr_employees', 'rectorate', 'sysadmin', 'accountant', 'student']
const PRACTICE_ROLES = ['student', 'teacher', 'methodist', 'dean', 'head_of_department']
const DIPLOMA_ROLES = ['student', 'teacher', 'dean', 'methodist', 'head_of_department']
const DISTANCE_ROLES = ['student', 'teacher', 'methodist', 'dean']
const DPO_ROLES = ['student', 'dpo_student', 'hr_students', 'hr_employees', 'methodist', 'rectorate', 'sysadmin']
const STUDENT_LIFE_ROLES = ['student', 'curator', 'teacher', 'dean', 'methodist', 'hr_students', 'hr_employees', 'rectorate', 'sysadmin']
const SCIENCE_ROLES = ['teacher', 'methodist', 'rectorate', 'sysadmin', 'dean']
const CAREER_ROLES = ['student', 'teacher', 'dean', 'hr_students', 'hr_employees', 'rectorate', 'sysadmin']
const WORKLOAD_ROLES = ['teacher', 'dean', 'head_of_department', 'methodist']
const MY_DISCIPLINES_ROLES = ['teacher']
const TAKE_TEST_ROLES = ['student']
const POSTGRAD_ROLES = ['postgrad', 'hr_students', 'hr_employees', 'methodist', 'dean', 'rectorate', 'sysadmin']
const STUDENT_HOME_ROLES = ['student', 'parent', 'foreign_student', 'postgrad', 'dpo_student']

const RouteLoading = () => {
  const { institution } = useInstitution()
  const location = useLocation()
  const certificatePath = isPublicCertificatePath(location.pathname)
  const universityOnly = certificatePath || /^\/(?:login\/?)?$/.test(location.pathname)
  const loadingInstitution = certificatePath && /\/BKC-/i.test(location.pathname) ? 'college' : universityOnly ? 'university' : institution
  return (
  <div
    role="status"
    aria-live="polite"
    aria-busy="true"
    style={{
      display: 'flex', alignItems: 'center', gap: 20, padding: 28,
      margin: '40px auto', maxWidth: 460, borderRadius: 18,
      background: 'var(--color-surface, #fff)', color: 'var(--color-text, #192238)',
      border: '1px solid var(--color-border, #e6e9ef)',
    }}
  >
    <img src={getInstitutionLogo(loadingInstitution)} width="64" height="80" alt="" style={{ flexShrink: 0, objectFit: 'contain' }} />
    <div>
      <strong style={{ display: 'block', fontSize: 18 }}>Загружаем раздел</strong>
      <span style={{ display: 'block', marginTop: 6, color: 'var(--color-text-muted, #677185)' }}>
        Готовим ваше рабочее пространство…
      </span>
    </div>
  </div>
  )
}

// The shell and permission checks remain mounted while an allowed page loads.
const load = (el) => <Suspense fallback={<RouteLoading />}>{el}</Suspense>
const wrap = (el, roles) => roles
  ? <ProtectedRoute requireRoles={roles}>{load(el)}</ProtectedRoute>
  : <ProtectedRoute>{load(el)}</ProtectedRoute>
const bare = (el) => <ProtectedRoute bare>{load(el)}</ProtectedRoute>

export const routes = [
  { path: '/', component: load(<Login />) },
  { path: '/login', component: load(<Login />) },
  { path: '/login/', component: load(<Login />) },
  { path: '/справка/:number', component: load(<PublicCertificatePage />) },
  { path: '/account-locked/', component: load(<AccountLockedPage />) },
  { path: '/post-login/', component: bare(<RoleHomeRedirect />) },

  { path: '/home/', component: wrap(<MainPage />) },

  { path: '/tasks/', component: wrap(<TasksPage />) },
  { path: '/task/', component: wrap(<TaskItemInfo isSubTask={false} />) },
  { path: '/subtask/', component: wrap(<TaskItemInfo isSubTask={true} />) },
  { path: '/statements/', component: wrap(<StatementPage />) },
  { path: '/statement/', component: wrap(<StatementInfo />) },
  { path: '/chats/', component: wrap(<ChatPageContainer />) },
  { path: '/chats/:id/', component: wrap(<ChatPageContainer />) },
  { path: '/register_person/', component: wrap(<RegisterForm />, HR_EMPLOYEES) },
  { path: '/statistic/', component: wrap(<StatisticPage />) },
  { path: '/all-statistic/', component: wrap(<AllStatisticPage />, ADMIN_ROLES) },
  { path: '/task-tree/', component: wrap(<TaskTree />) },
  { path: '/structure/', component: wrap(<StructurePage />) },
  { path: '/structure-sheme/:id/', component: wrap(<StructureInfo />) },
  { path: '/add-structure/', component: wrap(<StructureForm />, ADMIN_ROLES) },
  { path: '/answer-quiz/', component: wrap(<QuizForm />) },
  { path: '/calendar/', component: wrap(<CalendarPage />) },
  { path: '/report-card/', component: wrap(<ReportCard />) },
  { path: '/notes/', component: wrap(<NotesPage />) },

  { path: '/personalcard/', component: wrap(<PersonalCard />) },
  { path: '/europass/', component: wrap(<Europass />) },
  { path: '/finaleuro/', component: wrap(<FinalEuropass />) },
  { path: '/europassen/', component: wrap(<EuropassEn />) },
  { path: '/finaleuropassen/', component: wrap(<FinalEuropassEn />) },
  { path: '/europassky/', component: wrap(<EuropassKy />) },
  { path: '/finaleuropassky/', component: wrap(<FinalEuropassKy />) },
  { path: '/admin_organizations/', component: wrap(<AdminPage />, ADMIN_ROLES) },

  { path: '/test/:id', component: wrap(<PartWorkUsedAllUser />) },
  { path: '/tests/', component: wrap(<TestPage />) },
  { path: '/worktest/', component: wrap(<TestContent />) },
  { path: '/useworktest/', component: wrap(<PartWorkUsed />) },
  { path: '/test-builder/', component: wrap(<TestBuilderPage />, ['teacher', 'methodist', 'head_of_department', 'sysadmin']) },
  { path: '/test-builder/:id', component: wrap(<TestBuilderPage />, ['teacher', 'methodist', 'head_of_department', 'sysadmin']) },
  { path: '/question-bank/', component: wrap(<QuestionBankPage />, ['teacher', 'methodist', 'head_of_department', 'sysadmin']) },

  { path: '/surveys/', component: wrap(<SurveyPage />) },
  { path: '/survey/', component: wrap(<SurveyContent />) },
  { path: '/survey/:id', component: wrap(<PartSurveyUsedAllUser />) },
  { path: '/usesurvey/', component: wrap(<PartSurveyUsed />) },

  { path: '/accounting/', component: wrap(<AccountingPage />, ['accountant', 'rectorate', 'sysadmin']) },
  { path: '/accounting-info/', component: wrap(<AccountingDataPage />, ['accountant', 'rectorate', 'sysadmin']) },

  { path: '/archive/', component: wrap(<ArchivePage />, ['archivist', 'rectorate', 'sysadmin', 'hr_students', 'hr_employees']) },
  { path: '/archive/:archiveType/', component: wrap(<ArchiveDetail />, ['archivist', 'rectorate', 'sysadmin', 'hr_students', 'hr_employees']) },
  { path: '/archive/ocr-search/', component: wrap(<OcrSearchPage />, ['archivist', 'rectorate', 'sysadmin']) },
  { path: '/archive/order-link/', component: wrap(<OrderArchiveLinkPage />, ['archivist', 'rectorate', 'sysadmin']) },

  { path: '/student-home/', component: wrap(<StudentDashboard />, STUDENT_HOME_ROLES) },
  { path: '/teacher-home/', component: wrap(<TeacherDashboard />, TEACHER_HOME_ROLES) },
  // P1-A1: домашняя для роли applicant. Только сама роль; админам её показывать
  // нет смысла — пусть идут в /admin/admission/.
  { path: '/applicant-home/', component: wrap(<ApplicantHome />, ['applicant','student']) },
  { path: '/admission-applications/', component: wrap(<ApplicantHome />, ['hr_students','hr_employees']) },
  { path: '/student-finance/', component: wrap(<StudentFinancePage />, ['student', 'foreign_student', 'postgrad', 'dpo_student']) },
  { path: '/schedule/', component: wrap(<SchedulePage />) },
  { path: '/schedule-builder/', component: wrap(<ScheduleBuilderPage />, ADMIN_ROLES) },
  { path: '/schedule-import/', component: wrap(<ScheduleImportPage />, METHODIST) },
  { path: '/lesson-reschedule/', component: wrap(<LessonRescheduleModal />, ADMIN_ROLES) },
  { path: '/sessions/', component: wrap(<ExamSessionsPage />, METHODIST) },

  // Wave 4: дубль `/syllabus-view/` редиректим на `/my-syllabus/` (используется в StudentDashboard).
  { path: '/syllabus-view/', component: <Navigate to="/my-syllabus/" replace /> },
  { path: '/my-syllabus/', component: wrap(<SyllabusViewPage />, ['student', 'parent', 'teacher', 'curator']) },
  { path: '/syllabuses/', component: wrap(<SyllabusViewPage />, ADMIN_ROLES) },
  { path: '/syllabus-builder/', component: wrap(<SyllabusBuilderPage />, METHODIST) },
  { path: '/syllabus-builder/:id', component: wrap(<SyllabusBuilderPage />, METHODIST) },
  { path: '/syllabus-diff/', component: wrap(<SyllabusDiffPage />, METHODIST) },
  { path: '/syllabus-approval/', component: wrap(<SyllabusApprovalPage />, METHODIST) },

  { path: '/journal/', component: wrap(<JournalPage />, JOURNAL_ROLES) },
  { path: '/gradebook/', component: wrap(<GradebookPage />, GRADEBOOK_ROLES) },
  { path: '/absence-justification/', component: wrap(<AbsenceJustificationPage />, ['student', 'curator', 'dean', 'methodist', 'head_of_department']) },
  { path: '/curator-drilldown/', component: wrap(<CuratorDrilldownPage />, ['teacher', 'curator', 'dean', 'rectorate', 'sysadmin', 'methodist', 'head_of_department']) },
  { path: '/assignments-list/', component: wrap(<AssignmentsPage />, ['student', 'teacher', 'methodist', 'dean', 'curator', 'head_of_department']) },
  // Wave 4: `/statements-list/` устарел (в TeacherDashboard) → редиректим на `/teacher-statements/`.
  { path: '/statements-list/', component: <Navigate to="/teacher-statements/" replace /> },
  { path: '/teacher-statements/', component: wrap(<StatementsPage />, ['teacher', 'methodist', 'dean', 'head_of_department']) },

  { path: '/orders/', component: wrap(<OrdersPage />, ['hr_students', 'hr_employees', 'methodist', 'dean', 'rectorate', 'sysadmin']) },
  { path: '/groups/', component: wrap(<GroupsPage />, METHODIST) },
  { path: '/institution-structure/', component: wrap(<InstitutionStructurePage />, [...METHODIST, 'hr_students', 'hr_employees']) },
  { path: '/students-list/', component: wrap(<AdminFullPage initialTab="students" />, HR_STUDENTS) },
  { path: '/employees/', component: wrap(<AdminFullPage initialTab="employees" />, HR_EMPLOYEES) },
  { path: '/admission/', component: wrap(<AdminFullPage initialTab="enroll" />, ['hr_students', 'rectorate', 'sysadmin']) },
  { path: '/transfers/', component: wrap(<AdminFullPage initialTab="students" />, HR_STUDENTS) },
  { path: '/employee-wizard/', component: wrap(<EmployeeWizardPage />, HR_EMPLOYEES) },
  { path: '/employee-card/:id', component: wrap(<EmployeeCardPage />) },
  { path: '/work-requests/', component: wrap(<WorkRequestsPage />) },
  { path: '/student-lifecycle/:id', component: wrap(<StudentLifecyclePage />, HR_STUDENTS) },

  { path: '/library/', component: wrap(<LibraryPage />) },
  { path: '/dorm/', component: wrap(<DormPage />, DORM_ROLES) },
  { path: '/practice/', component: wrap(<PracticePage />, PRACTICE_ROLES) },
  { path: '/diploma/', component: wrap(<DiplomaPage />, DIPLOMA_ROLES) },
  { path: '/take-test/:id', component: wrap(<TestTakingPage />, TAKE_TEST_ROLES) },
  { path: '/dean-dashboard/', component: wrap(<DeanDashboard />, DEAN) },
  { path: '/subject/:id/', component: wrap(<SubjectPage />) },

  { path: '/reports/', component: wrap(<ReportsPage />, [...ADMIN_ROLES, 'teacher', 'curator']) },
  { path: '/reports/form-1nk/', component: wrap(<Form1NKPage />, ADMIN_ROLES) },
  { path: '/reports/contingent/', component: wrap(<ContingentReportPage />, ADMIN_ROLES) },
  { path: '/reports/accreditation/', component: wrap(<AccreditationReportPage />, ADMIN_ROLES) },
  { path: '/admin/audit/', component: wrap(<AdminFullPage initialTab="audit" />, ['sysadmin']) },

  { path: '/distance/', component: wrap(<DistanceLearningPage />, DISTANCE_ROLES) },
  { path: '/distance/lectures/', component: wrap(<VideoLecturesPage />, DISTANCE_ROLES) },
  { path: '/distance/virtual/', component: wrap(<VirtualClassroomPage />, DISTANCE_ROLES) },
  { path: '/distance/virtual/:id', component: wrap(<VirtualClassroomPage />, DISTANCE_ROLES) },

  { path: '/postgrad/', component: wrap(<PostgradPage />, POSTGRAD_ROLES) },
  { path: '/foreign-students/', component: wrap(<ForeignStudentsPage />, HR_STUDENTS) },
  { path: '/military-students/', component: wrap(<MilitaryStudentPage />, HR_STUDENTS) },
  { path: '/dpo/', component: wrap(<DpoPage />, DPO_ROLES) },
  { path: '/student-life/', component: wrap(<StudentLifePage />, STUDENT_LIFE_ROLES) },
  { path: '/science/', component: wrap(<SciencePage />, SCIENCE_ROLES) },
  { path: '/career/', component: wrap(<CareerPage />, CAREER_ROLES) },
  { path: '/plagiarism/', component: wrap(<PlagiarismPage />, ['teacher', 'methodist', 'dean', 'rectorate', 'sysadmin']) },

  { path: '/help/', component: wrap(<HelpPage />) },
  { path: '/help/api/', component: wrap(<ApiDocsPage />, ['sysadmin']) },
  { path: '/admin/integrations/', component: wrap(<IntegrationsPage />, ['sysadmin', 'rectorate']) },
  // Промежуток-101%: каталог сервисов платформы — только для суперадмина и ректората.
  { path: '/admin/services/', component: wrap(<ServicesPage />, ['sysadmin', 'rectorate']) },

  { path: '/create-statement/', component: wrap(<StatementPage />) },
  { path: '/statement-history/', component: wrap(<StatementPage />) },
  { path: '/workload/', component: wrap(<TeacherDashboard />, WORKLOAD_ROLES) },
  { path: '/my-disciplines/', component: wrap(<JournalPage />, MY_DISCIPLINES_ROLES) },
  // Wave 4: alias `/admin-full/` → `/admin/` для обратной совместимости (см. ReportsPage).
  { path: '/admin-full/', component: <Navigate to="/admin/" replace /> },

  {
    path: '/admin/',
    component: wrap(<AdminFullPage />, ADMIN_ROLES),
  },
]
